import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, Trash2, Clock, Coffee, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { shiftSchema, ShiftFormData } from "@/schemas/shiftSchemas";
import { Shift, ShiftBreak, ShiftDaySchedule, SHIFT_COLORS, DAY_NAMES, DAY_NAMES_SHORT } from "@/types/shifts";
import { useCreateShift, useUpdateShift, useBreakTypes, useShiftBreaks, useCreateShiftBreak, useDeleteShiftBreak } from "@/hooks/useShifts";
import { api } from "@/lib/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface ShiftFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shift?: Shift | null;
}

interface DayScheduleDraft {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_working_day: boolean;
}

interface BreakDraft {
  id?: string;
  break_type_id: string;
  start_offset_minutes: number;
  duration_minutes: number;
  is_mandatory: boolean;
}

export function ShiftFormDialog({
  open,
  onOpenChange,
  shift,
}: ShiftFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateShift();
  const updateMutation = useUpdateShift();
  const createBreakMutation = useCreateShiftBreak();
  const deleteBreakMutation = useDeleteShiftBreak();
  const isLoading = createMutation.isPending || updateMutation.isPending;

  const [useCustomSchedule, setUseCustomSchedule] = useState(false);
  const [daySchedules, setDaySchedules] = useState<DayScheduleDraft[]>([]);
  const [breaks, setBreaks] = useState<BreakDraft[]>([]);
  const [initialBreakIds, setInitialBreakIds] = useState<string[]>([]);

  const { data: breakTypes } = useBreakTypes(true);
  const { data: existingBreaks } = useShiftBreaks(shift?.id || '');

  // Load day schedules when editing
  const { data: existingDaySchedules } = useQuery({
    queryKey: ['shift-day-schedules', shift?.id],
    queryFn: async () => {
      if (!shift?.id) return [];
      const { data, error } = await api
        .from('shift_day_schedules')
        .select('*')
        .eq('shift_id', shift.id)
        .order('day_of_week');
      if (error) throw error;
      return data as ShiftDaySchedule[];
    },
    enabled: !!shift?.id && open,
  });

  const form = useForm<ShiftFormData>({
    resolver: zodResolver(shiftSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
      start_time: "06:00",
      end_time: "14:00",
      crosses_midnight: false,
      color: "#3b82f6",
      is_active: true,
    },
  });

  // Initialize form when shift changes
  useEffect(() => {
    if (!open) return;

    if (shift) {
      form.reset({
        name: shift.name,
        code: shift.code,
        description: shift.description || "",
        start_time: shift.start_time.slice(0, 5),
        end_time: shift.end_time.slice(0, 5),
        crosses_midnight: shift.crosses_midnight,
        color: shift.color,
        is_active: shift.is_active,
      });
    } else {
      form.reset({
        name: "",
        code: "",
        description: "",
        start_time: "06:00",
        end_time: "14:00",
        crosses_midnight: false,
        color: "#3b82f6",
        is_active: true,
      });
    }
  }, [shift, form, open]);

  // Initialize day schedules
  useEffect(() => {
    if (!open) return;

    if (existingDaySchedules && existingDaySchedules.length > 0) {
      setUseCustomSchedule(true);
      setDaySchedules(existingDaySchedules.map(ds => ({
        day_of_week: ds.day_of_week,
        start_time: ds.start_time.slice(0, 5),
        end_time: ds.end_time.slice(0, 5),
        is_working_day: ds.is_working_day,
      })));
    } else {
      setUseCustomSchedule(false);
      // Initialize with default schedule for all days (Mon-Fri working, Sat-Sun off)
      const defaultSchedule = form.getValues();
      setDaySchedules([
        { day_of_week: 0, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: false }, // Dom
        { day_of_week: 1, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: true },  // Lun
        { day_of_week: 2, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: true },  // Mar
        { day_of_week: 3, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: true },  // Mie
        { day_of_week: 4, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: true },  // Jue
        { day_of_week: 5, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: true },  // Vie
        { day_of_week: 6, start_time: defaultSchedule.start_time, end_time: defaultSchedule.end_time, is_working_day: false }, // Sab
      ]);
    }
  }, [open, existingDaySchedules, form]);

  // Initialize breaks
  useEffect(() => {
    if (!open) return;

    if (existingBreaks && existingBreaks.length > 0) {
      setInitialBreakIds(existingBreaks.map(b => b.id));
      setBreaks(existingBreaks.map(b => ({
        id: b.id,
        break_type_id: b.break_type_id,
        start_offset_minutes: b.start_offset_minutes,
        duration_minutes: b.duration_minutes,
        is_mandatory: b.is_mandatory,
      })));
    } else {
      setInitialBreakIds([]);
      setBreaks([]);
    }
  }, [open, existingBreaks]);

  const updateDaySchedule = (dayOfWeek: number, field: keyof DayScheduleDraft, value: any) => {
    setDaySchedules(prev => prev.map(ds =>
      ds.day_of_week === dayOfWeek ? { ...ds, [field]: value } : ds
    ));
  };

  const addBreak = () => {
    const defaultBreakType = breakTypes?.[0];
    setBreaks(prev => [...prev, {
      break_type_id: defaultBreakType?.id || '',
      start_offset_minutes: 120,
      duration_minutes: defaultBreakType?.default_duration_minutes || 30,
      is_mandatory: true,
    }]);
  };

  const updateBreak = (index: number, field: keyof BreakDraft, value: any) => {
    setBreaks(prev => prev.map((b, i) => {
      if (i !== index) return b;
      const updated = { ...b, [field]: value };
      // Auto-fill duration when break type changes
      if (field === 'break_type_id' && breakTypes) {
        const bt = breakTypes.find(t => t.id === value);
        if (bt) updated.duration_minutes = bt.default_duration_minutes;
      }
      return updated;
    }));
  };

  const removeBreak = (index: number) => {
    setBreaks(prev => prev.filter((_, i) => i !== index));
  };

  const calculateBreakTime = (startOffset: number) => {
    const baseTime = form.watch('start_time') || '06:00';
    const [h, m] = baseTime.split(':').map(Number);
    const totalMinutes = h * 60 + m + startOffset;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const mins = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const onSubmit = async (data: ShiftFormData) => {
    try {
      let shiftId: string;

      if (shift) {
        await updateMutation.mutateAsync({ id: shift.id, ...data });
        shiftId = shift.id;
      } else {
        const result = await createMutation.mutateAsync(data);
        shiftId = result.id;
      }

      // Save day schedules if using custom schedule
      if (useCustomSchedule) {
        // Delete existing schedules
        await api.from('shift_day_schedules').delete().eq('shift_id', shiftId);
        // Insert new schedules
        const schedulesToInsert = daySchedules.map(ds => ({
          shift_id: shiftId,
          day_of_week: ds.day_of_week,
          start_time: ds.start_time,
          end_time: ds.end_time,
          is_working_day: ds.is_working_day,
        }));
        const { error: scheduleError } = await api.from('shift_day_schedules').insert(schedulesToInsert);
        if (scheduleError) throw scheduleError;
      } else {
        // Delete any existing day schedules if not using custom
        await api.from('shift_day_schedules').delete().eq('shift_id', shiftId);
      }

      // Handle breaks - delete removed, add new
      const currentBreakIds = breaks.filter(b => b.id).map(b => b.id!);
      const breaksToDelete = initialBreakIds.filter(id => !currentBreakIds.includes(id));

      for (const breakId of breaksToDelete) {
        await deleteBreakMutation.mutateAsync(breakId);
      }

      for (const breakData of breaks) {
        if (!breakData.id) {
          // New break
          await createBreakMutation.mutateAsync({
            shift_id: shiftId,
            break_type_id: breakData.break_type_id,
            start_offset_minutes: breakData.start_offset_minutes,
            duration_minutes: breakData.duration_minutes,
            is_mandatory: breakData.is_mandatory,
          });
        }
        // Note: We don't update existing breaks for simplicity - user can delete and re-add
      }

      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shift-breaks'] });
      queryClient.invalidateQueries({ queryKey: ['shift-day-schedules'] });

      toast({
        title: shift ? "Turno actualizado" : "Turno creado",
        description: "Los cambios se han guardado correctamente",
      });
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al guardar el turno",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {shift ? "Editar Turno" : "Nuevo Turno"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general" className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> General
                </TabsTrigger>
                <TabsTrigger value="schedule" className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> Horarios
                </TabsTrigger>
                <TabsTrigger value="breaks" className="flex items-center gap-1">
                  <Coffee className="w-4 h-4" /> Descansos
                  {breaks.length > 0 && <Badge variant="secondary" className="ml-1">{breaks.length}</Badge>}
                </TabsTrigger>
              </TabsList>

              {/* TAB: General */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre</FormLabel>
                        <FormControl>
                          <Input placeholder="Turno Mañana" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Código</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="M"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <FormDescription>Identificador único</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="start_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hora Inicio (por defecto)</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="end_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hora Fin (por defecto)</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Descripción del turno..."
                          className="resize-none"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Color</FormLabel>
                      <div className="flex gap-2 flex-wrap">
                        {SHIFT_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              field.value === color.value
                                ? "border-foreground scale-110"
                                : "border-transparent"
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => field.onChange(color.value)}
                            title={color.name}
                          />
                        ))}
                        <Input
                          type="color"
                          className="w-8 h-8 p-0 border-0 cursor-pointer"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-6">
                  <FormField
                    control={form.control}
                    name="crosses_midnight"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="!mt-0 cursor-pointer">
                          Cruza medianoche
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="!mt-0 cursor-pointer">
                          Activo
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              {/* TAB: Horarios por día */}
              <TabsContent value="schedule" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                  <div>
                    <div className="font-medium text-sm">Horarios personalizados por día</div>
                    <div className="text-xs text-muted-foreground">
                      Activa para definir horarios diferentes cada día de la semana
                    </div>
                  </div>
                  <Switch
                    checked={useCustomSchedule}
                    onCheckedChange={(checked) => {
                      setUseCustomSchedule(checked);
                      if (checked) {
                        // Copy default times to all days
                        const defaultStart = form.getValues('start_time');
                        const defaultEnd = form.getValues('end_time');
                        setDaySchedules(prev => prev.map(ds => ({
                          ...ds,
                          start_time: defaultStart,
                          end_time: defaultEnd,
                        })));
                      }
                    }}
                  />
                </div>

                {useCustomSchedule ? (
                  <div className="space-y-2">
                    {daySchedules.sort((a, b) => {
                      // Sort: Lunes(1) first, then Martes(2)..Sábado(6), Domingo(0) last
                      const order = [1, 2, 3, 4, 5, 6, 0];
                      return order.indexOf(a.day_of_week) - order.indexOf(b.day_of_week);
                    }).map((ds) => (
                      <div
                        key={ds.day_of_week}
                        className={`flex flex-col gap-2 sm:grid sm:grid-cols-[100px_1fr_1fr_80px] sm:gap-3 items-center p-2 rounded-lg border ${
                          ds.is_working_day ? 'bg-background' : 'bg-muted/50'
                        }`}
                      >
                        <div className="font-medium text-sm">
                          {DAY_NAMES[ds.day_of_week]}
                        </div>
                        <Input
                          type="time"
                          value={ds.start_time}
                          onChange={(e) => updateDaySchedule(ds.day_of_week, 'start_time', e.target.value)}
                          disabled={!ds.is_working_day}
                          className="h-9"
                        />
                        <Input
                          type="time"
                          value={ds.end_time}
                          onChange={(e) => updateDaySchedule(ds.day_of_week, 'end_time', e.target.value)}
                          disabled={!ds.is_working_day}
                          className="h-9"
                        />
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={ds.is_working_day}
                            onCheckedChange={(v) => updateDaySchedule(ds.day_of_week, 'is_working_day', v)}
                          />
                          <span className="text-xs text-muted-foreground">
                            {ds.is_working_day ? 'Sí' : 'No'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Se usará el mismo horario para todos los días</p>
                    <p className="text-xs mt-1">
                      {form.watch('start_time')} - {form.watch('end_time')}
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* TAB: Descansos */}
              <TabsContent value="breaks" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">Descansos del turno</div>
                    <div className="text-xs text-muted-foreground">
                      Define los descansos obligatorios dentro del turno
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addBreak}>
                    <Plus className="w-4 h-4 mr-1" /> Agregar descanso
                  </Button>
                </div>

                {breaks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                    <Coffee className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No hay descansos configurados</p>
                    <p className="text-xs mt-1">Agrega descansos obligatorios para este turno</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {breaks.map((brk, index) => (
                      <div key={brk.id || index} className="border rounded-lg p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
                            Descanso {index + 1}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBreak(index)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FormLabel>Tipo de descanso</FormLabel>
                            <Select
                              value={brk.break_type_id}
                              onValueChange={(v) => updateBreak(index, 'break_type_id', v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {breakTypes?.map((bt) => (
                                  <SelectItem key={bt.id} value={bt.id}>
                                    {bt.name} ({bt.default_duration_minutes} min)
                                    {bt.is_paid && " - Pagado"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <FormLabel>Hora del descanso</FormLabel>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={720}
                                value={brk.start_offset_minutes}
                                onChange={(e) => updateBreak(index, 'start_offset_minutes', parseInt(e.target.value) || 0)}
                                className="w-20"
                              />
                              <span className="text-sm text-muted-foreground">min desde inicio</span>
                              <Badge variant="outline">{calculateBreakTime(brk.start_offset_minutes)}</Badge>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FormLabel>Duración (minutos)</FormLabel>
                            <Input
                              type="number"
                              min={1}
                              max={120}
                              value={brk.duration_minutes}
                              onChange={(e) => updateBreak(index, 'duration_minutes', parseInt(e.target.value) || 30)}
                            />
                          </div>
                          <div className="flex items-end pb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={brk.is_mandatory}
                                onCheckedChange={(v) => updateBreak(index, 'is_mandatory', !!v)}
                              />
                              <span className="text-sm">Descanso obligatorio</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {shift ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
